import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Col, Container, Form, Input, Label, Row, Spinner } from "reactstrap";

import { submitContactMessage } from "../../helpers/firestoreService";
import porcherMark from "../../assets/images/porcher-logo.png";
import porcherWordmark from "../../assets/images/porcher-wordmark.png";
import "../Landing/landing.scss";

const INITIAL_FORM = {
  name: "",
  email: "",
  subject: "",
  message: "",
  company: "",
};

const ContactPage = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    document.title = "Contact | Porcher";
  }, []);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (form.company) return;

    setSubmitting(true);
    setResult(null);
    try {
      await submitContactMessage(form);
      setForm(INITIAL_FORM);
      setResult({ type: "success", message: "Thanks for reaching out. We received your message." });
    } catch (error) {
      console.error("Contact form submission failed:", error);
      setResult({
        type: "danger",
        message: "We couldn't send your message. Please try again or email contact@porcher.ai.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="porcher-page porcher-contact-page">
      <nav className="porcher-navbar py-3">
        <Container className="d-flex align-items-center justify-content-between">
          <Link className="porcher-brand" to="/">
            <img src={porcherMark} alt="" className="p-mark" />
            <img src={porcherWordmark} alt="Porcher" className="p-wordmark" />
          </Link>
          <div className="d-flex align-items-center gap-2">
            <Link to="/" className="porcher-nav-link">Home</Link>
            <Link to="/login" className="porcher-nav-link">Sign In</Link>
            <Link to="/register" className="p-btn p-btn-primary d-none d-md-inline-flex">
              List Your Property
            </Link>
          </div>
        </Container>
      </nav>

      <main className="porcher-contact-main">
        <Container>
          <Row className="g-5 align-items-start">
            <Col lg={5}>
              <span className="porcher-eyebrow">Contact Porcher</span>
              <h1>How can we help?</h1>
              <p className="porcher-contact-lead">
                Send us a message about your account, the platform, partnerships, or anything else on your mind.
              </p>
              <div className="porcher-contact-options">
                <a className="porcher-contact-email" href="mailto:contact@porcher.ai">
                  <i className="bx bx-envelope" aria-hidden="true" />
                  <span>
                    <small>Email us directly</small>
                    contact@porcher.ai
                  </span>
                </a>
                <a className="porcher-contact-email" href="tel:+17028991226">
                  <i className="bx bx-phone" aria-hidden="true" />
                  <span>
                    <small>Call us directly</small>
                    702-899-1226
                  </span>
                </a>
              </div>
            </Col>

            <Col lg={7}>
              <div className="porcher-contact-form-wrap">
                <Form onSubmit={handleSubmit}>
                  <div className="porcher-honeypot" aria-hidden="true">
                    <Label for="company">Company</Label>
                    <Input
                      id="company"
                      name="company"
                      value={form.company}
                      onChange={handleChange}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  <Row className="g-3">
                    <Col md={6}>
                      <Label for="contact-name">Name</Label>
                      <Input
                        id="contact-name"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        minLength={2}
                        maxLength={100}
                        autoComplete="name"
                        required
                      />
                    </Col>
                    <Col md={6}>
                      <Label for="contact-email">Email</Label>
                      <Input
                        id="contact-email"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        maxLength={254}
                        autoComplete="email"
                        required
                      />
                    </Col>
                    <Col xs={12}>
                      <Label for="contact-subject">Subject</Label>
                      <Input
                        id="contact-subject"
                        name="subject"
                        value={form.subject}
                        onChange={handleChange}
                        minLength={2}
                        maxLength={150}
                        required
                      />
                    </Col>
                    <Col xs={12}>
                      <Label for="contact-message">Message</Label>
                      <Input
                        id="contact-message"
                        name="message"
                        type="textarea"
                        rows={7}
                        value={form.message}
                        onChange={handleChange}
                        minLength={10}
                        maxLength={5000}
                        required
                      />
                    </Col>
                  </Row>

                  {result && (
                    <Alert color={result.type} className="mt-4 mb-0" aria-live="polite">
                      {result.message}
                    </Alert>
                  )}

                  <button type="submit" className="p-btn p-btn-primary mt-4" disabled={submitting}>
                    {submitting ? <Spinner size="sm" /> : <i className="bx bx-send" aria-hidden="true" />}
                    {submitting ? "Sending..." : "Send Message"}
                  </button>
                </Form>
              </div>
            </Col>
          </Row>
        </Container>
      </main>

      <footer className="porcher-footer">
        <Container>
          <Row>
            <Col lg={5} className="mb-4 mb-lg-0">
              <img src={porcherWordmark} alt="Porcher" className="p-wordmark" />
              <p>Everything a self-managed homeowner needs to rent and manage a property.</p>
              <div className="porcher-footer-contact">
                <a href="mailto:contact@porcher.ai">contact@porcher.ai</a>
                <a href="tel:+17028991226">702-899-1226</a>
              </div>
            </Col>
            <Col lg={3} md={6} className="mb-4 mb-lg-0">
              <h6>Product</h6>
              <ul>
                <li><Link to="/pricing">Pricing</Link></li>
                <li><Link to="/register">List Your Property</Link></li>
                <li><Link to="/login">Sign In</Link></li>
              </ul>
            </Col>
            <Col lg={4} md={6}>
              <h6>Legal</h6>
              <ul>
                <li><Link to="/terms-of-service">Terms of Service</Link></li>
                <li><Link to="/privacy-policy">Privacy Policy</Link></li>
              </ul>
            </Col>
          </Row>
          <div className="porcher-footer-bottom">
            &copy; {new Date().getFullYear()} Porcher. All rights reserved.
          </div>
        </Container>
      </footer>
    </div>
  );
};

export default ContactPage;